import { useState } from "react";

import {
  DesktopTemplate,
  MobileTemplate,
  TemplatePreview,
  TemplateTypeSelection,
} from "features/templates/components";
import CreateOrEditTemplate from "features/templates/components/CreateOrEditTemplate";
import type {
  TemplateSchemaType,
  VoiceTemplateSchemaType,
} from "features/templates/schemas";
import type { CreatedTemplate, TemplateType } from "features/templates/types";

import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "common/components/ui/Dialog";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import { useCampaignContext } from "../../../../context/CampaignContext";
import useAudience from "../../../../datahooks/useAudience";

interface NewTemplateProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  typeOptions: TemplateType[];
  onCreated: (createdTemplate: CreatedTemplate) => void;
}

export default function NewTemplate({
  isOpen,
  setIsOpen,
  typeOptions,
  onCreated,
}: NewTemplateProps) {
  const isDesktop = useTwBreakpoint("lg");
  const { owners } = useCampaignContext();
  const { audience } = useAudience();
  const { accounts } = useSelectedWorkspaceContext();

  const initialTemplateType = typeOptions.length === 1 ? typeOptions[0] : null;
  const [templateType, setTemplateType] =
    useState<TemplateType>(initialTemplateType);
  const [previewTemplate, setPreviewTemplate] =
    useState<Exclude<TemplateSchemaType, VoiceTemplateSchemaType>>();

  const senderProfiles = accounts.filter(({ id }) => owners.includes(id));
  const targetProfiles = audience?.slice(0, 3);

  const mainComponent = templateType ? (
    <CreateOrEditTemplate onSuccess={onCreated} initialData={templateType}>
      {(onSubmit, isLoading, form) => {
        const updatedTemplate = form.watch();
        const templatePreviewComponent = updatedTemplate.type !==
          "VOICE_TEMPLATE" && (
          <TemplatePreview
            previewTemplate={previewTemplate}
            updatedTemplate={updatedTemplate}
            onUpdatePreview={() => setPreviewTemplate(updatedTemplate)}
            senderProfiles={senderProfiles}
            targetProfiles={targetProfiles}
            hasError={"message" in form.formState.errors}
          />
        );
        const saveButton = (
          <Button onClick={onSubmit} isLoading={isLoading}>
            Save
          </Button>
        );
        return isDesktop ? (
          <>
            <DialogBody className="gap-y-4">
              <DesktopTemplate
                previewComponent={templatePreviewComponent}
                form={form}
                isCreatingNew
              />
            </DialogBody>
            <DialogFooter className="justify-end">
              <Button
                variant="secondary-black"
                disabled={isLoading}
                onClick={() => {
                  sessionStorage.removeItem(templateType.toUpperCase());
                  setIsOpen(false);
                }}
              >
                Discard
              </Button>
              {saveButton}
            </DialogFooter>
          </>
        ) : (
          <MobileTemplate
            onClose={() => setIsOpen(false)}
            previewComponent={templatePreviewComponent}
            updatePreview={() => {
              if (updatedTemplate.type !== "VOICE_TEMPLATE") {
                setPreviewTemplate(updatedTemplate);
              }
            }}
            saveButton={saveButton}
            form={form}
          />
        );
      }}
    </CreateOrEditTemplate>
  ) : (
    <TemplateTypeSelection
      allowedTypes={typeOptions}
      templateType={templateType}
      setTemplateType={setTemplateType}
    />
  );

  if (isDesktop) {
    return (
      <Dialog
        open={isOpen}
        onOpenChange={setIsOpen}
        onAfterClose={() => {
          setTemplateType(initialTemplateType);
          setPreviewTemplate(undefined);
        }}
      >
        <DialogContent size={templateType ? "xl" : "md"}>
          <DialogHeader
            variant={templateType ? "border" : "default"}
            title="Create template"
          />
          {mainComponent}
        </DialogContent>
      </Dialog>
    );
  }
  if (isOpen) {
    return mainComponent;
  }
}
